/**
 * Store pricing arithmetic — the single definition of what a buyer pays.
 *
 * Lifted because the products grid and the product form each computed it
 * separately and disagreed: the grid showed the ex-GST selling price while the
 * form's headline showed it incl. GST, so one product read as two prices.
 * Both now call these, so they cannot drift apart again.
 *
 * All integer paise via `BigInt` — the same truncating arithmetic the server
 * uses for `sellingPricePaise` (`apps/api/src/routers/store-catalog.ts`), so
 * the client never shows a figure the server wouldn't compute. Doing this in
 * floats would drift a paisa on some values.
 */

/** Basis-point denominator: 10 000 bps = 100%. */
const BPS = 10_000n;

/**
 * Selling price before tax — `base × (1 − discount)`.
 *
 * This is the figure the server stores as `sellingPricePaise`. GST is applied
 * at invoicing, not here, so this is the number the *record* carries.
 */
export function sellingPaise(basePaise: bigint, discountBps: number): bigint {
  const discount = BigInt(Math.max(0, Math.min(10_000, Math.round(discountBps))));
  return (basePaise * (BPS - discount)) / BPS;
}

/** Adds GST to an ex-tax figure — what actually lands on the buyer's invoice. */
export function withGstPaise(exTaxPaise: bigint, taxBps: number | null): bigint {
  const tax = BigInt(Math.max(0, Math.min(10_000, Math.round(taxBps ?? 0))));
  return (exTaxPaise * (BPS + tax)) / BPS;
}

/**
 * Price of one pack — `per-piece × pieces`.
 *
 * **Derived, never stored.** `docs/ecom/prd.md` §6.2 keeps the per-piece figure
 * as the only price on record ("never stored twice") because two things depend
 * on it: the GST slab turns on the per-piece value (§6.1 pt 5 — 5% at or below
 * ₹2,500/piece, 18% above), and `items.default_rate_paise` pre-fills invoice
 * line rates against a quantity counted in pieces. A pack figure in that column
 * would be wrong on both counts.
 *
 * Returns `0n` for a non-positive or fractional piece count, so a half-filled
 * form shows nothing rather than a misleading number.
 */
export function packPaise(perPiecePaise: bigint, pieces: number): bigint {
  if (!Number.isInteger(pieces) || pieces <= 0) return 0n;
  return perPiecePaise * BigInt(pieces);
}

/**
 * A pack price split back to the per-piece rate the record stores.
 *
 * The form lets a pack-sold product be priced the way the trade quotes it —
 * "₹1,710 the set" — but `items.default_rate_paise` is per piece and has to
 * stay that way: it pre-fills invoice line rates against a quantity counted in
 * pieces, and the GST slab turns on the per-piece value (`prd.md` §6.1 pt 5).
 * So the pack figure is entered and immediately divided.
 *
 * Rounds half up rather than truncating, so ₹1,000 over 6 stores ₹166.67 and
 * not ₹166.66. **The division is lossy** when the pack price isn't divisible —
 * ₹1,000 ÷ 6 × 6 comes back ₹1,000.02 — which is why the form echoes the
 * recomputed pack total instead of hiding it.
 */
export function perPiecePaise(packPricePaise: bigint, units: number): bigint {
  if (!Number.isInteger(units) || units <= 1) return packPricePaise;
  const u = BigInt(units);
  return (packPricePaise * 2n + u) / (u * 2n);
}

/** The three figures every admin price display shows (spec 13, R6a). */
export interface PriceChain {
  /** Before the discount, incl-GST — the struck-through "was". */
  wasPaise: bigint;
  /** After the discount, incl-GST — the headline. */
  finalPaise: bigint;
  /** `was − final`. What the discount is worth in rupees. */
  savingPaise: bigint;
}

/**
 * `was → discount → final` for one priced thing, **all three incl-GST**.
 *
 * Both ends of the comparison must sit on the same tax basis (spec 13 R6a): a
 * struck-through ex-GST price beside an incl-GST final made a −5% discount
 * render as a rise from ₹7,000.00 to ₹7,248.50 — each figure right, the pair
 * meaningless.
 *
 * `savingPaise` is computed here as `was − final` rather than
 * `was × discountBps`, and that is the point of the function: `withGstPaise`
 * truncates, so a saving derived independently can leave the three displayed
 * numbers failing to add up. Lifted because the products grid, the product
 * detail card and the pricing-entry card each had their own copy — three
 * chances for the rule to drift.
 */
export function priceChainPaise(
  pricePaise: bigint,
  discountBps: number,
  taxBps: number | null,
): PriceChain {
  const wasPaise = withGstPaise(pricePaise, taxBps);
  const finalPaise = withGstPaise(sellingPaise(pricePaise, discountBps), taxBps);
  return { wasPaise, finalPaise, savingPaise: wasPaise - finalPaise };
}

/** Every figure the price preview shows, in integer paise. */
export interface PricePreview {
  /** Ex-GST, for the unit of sale — one piece, or the whole pack. */
  exTaxPaise: bigint;
  /** Incl-GST, for the unit of sale. The headline figure. */
  inclTaxPaise: bigint;
  /** The GST component of `inclTaxPaise`. */
  gstPaise: bigint;
  /** Incl-GST for one piece — the sub-line when the unit of sale is a pack. */
  perPieceInclTaxPaise: bigint;
  /** Incl-GST before the discount — the "was" figure, same unit of sale. */
  wasInclTaxPaise: bigint;
}

/**
 * The preview block's arithmetic, in one place so it can be tested without
 * rendering.
 *
 * `pieces` is the unit of sale: pass the pack's composition total to price a
 * pack, or 0/1 for a loose piece. **GST is applied after multiplying**, not
 * before — `pack × (1 − discount)` then GST, which is the order an invoice line
 * uses (rate × qty, then tax on the line total). Doing it the other way rounds
 * per piece and drifts a paisa or two on the pack.
 *
 * The stored price is per piece regardless (`prd.md` §6.2); everything here is
 * display.
 */
export function pricePreview(
  basePaise: bigint,
  discountBps: number,
  taxBps: number,
  pieces: number,
): PricePreview {
  const units = Number.isInteger(pieces) && pieces > 0 ? pieces : 1;
  const perPieceExTax = sellingPaise(basePaise, discountBps);
  const exTax = packPaise(perPieceExTax, units);
  const inclTax = withGstPaise(exTax, taxBps);
  return {
    exTaxPaise: exTax,
    inclTaxPaise: inclTax,
    gstPaise: inclTax - exTax,
    perPieceInclTaxPaise: withGstPaise(perPieceExTax, taxBps),
    wasInclTaxPaise: withGstPaise(packPaise(basePaise, units), taxBps),
  };
}

/** Rupees as typed (possibly partial or invalid) → integer paise, never NaN. */
export function rupeesToPaise(rupees: string): bigint {
  const n = Number.parseFloat(rupees);
  if (!Number.isFinite(n) || n <= 0) return 0n;
  return BigInt(Math.round(n * 100));
}
